import { MAX_PERSONEN } from "../../shared/personen.ts";
import type { Umriss } from "../../shared/umriss.ts";

/**
 * Die Personenzahl als Pflichtwahl ohne Startwert (Annas Mail vom 08.10.2026).
 *
 * **Warum es das gibt:** bis zum 08.10.2026 stand die Zahl auf 1, und „Weiter" hing allein
 * am Umriss. Annas Testpersonen (Eltern, Partner) haben sie durchweg übersehen, und die
 * Zählprüfung am Ende wurde weggetippt, obwohl die Zahl teils nicht stimmte. Eine
 * Voreinstellung, die meistens stimmt, sieht niemand an — also gibt es keine mehr, und
 * „Weiter" wartet, bis gewählt ist.
 *
 * **Warum Schnellwahl und kein Pop-up:** Annas erster Vorschlag war eine Rückfrage nach
 * jedem Bild. Eine Ja/Nein-Frage, deren Ja die Voreinstellung bestätigt, ist dasselbe
 * Muster wie die Zählprüfung, die schon weggetippt wurde. Variantenvergleich am
 * 08.10.2026 (`TODO.md`): „1 · 2 · 3 · mehr" gegen −/+ ohne Startwert; die Schnellwahl
 * zeigt die Antworten, statt sie hinter einem „+" zu verstecken, und die häufigen Fälle
 * kosten je einen Tipp.
 *
 * ⚠️ **Geldwirksam:** die Zahl trägt in V1 den Preis (`shared/personen.ts`).
 */

/** Die Zahlen, die als eigener Knopf dastehen. Darüber übernimmt der Zähler. */
export const SCHNELLWAHL = [1, 2, 3] as const;

/** Wo „mehr" den Zähler beginnen lässt — die erste Zahl, die keinen eigenen Knopf hat.
 *  Folgt aus `SCHNELLWAHL`; ein Test hält die beiden zusammen. */
export const MEHR_START = 4;

/**
 * Womit die Wahl beginnt.
 *
 * Ein Bild **ohne** Umriss ist noch nie durch „Weiter" gegangen: seine Zahl ist der
 * Standardwert aus der Datenbank (`migrations/0005_personen.sql`), nicht die Antwort von
 * jemandem — also keine. Ein Bild **mit** Umriss trägt eine Zahl, die schon einmal
 * gewählt wurde; wer zum Nachbessern zurückkommt, soll sie nicht noch einmal angeben.
 */
export function startwert(bild: { umriss: Umriss | null; personen: number }): number | null {
  return bild.umriss === null ? null : bild.personen;
}

export function kannWeiter(umriss: Umriss | null, personen: number | null): boolean {
  return umriss !== null && personen !== null;
}

/**
 * Zähler statt Schnellwahl?
 *
 * Hängt allein an der Zahl und nicht an einem eigenen Schalter „mehr ist offen". Daraus
 * folgt der Rückweg ohne Extraknopf: wer im Zähler unter 4 geht, steht wieder in der
 * Schnellwahl, mit der 3 gewählt.
 */
export function zeigtZaehler(wert: number | null): boolean {
  return wert !== null && wert >= MEHR_START;
}

export function weniger(wert: number): number {
  return Math.max(1, wert - 1);
}

export function mehr(wert: number): number {
  return Math.min(MAX_PERSONEN, wert + 1);
}

/** Der Satz, wenn jemand auf das graue „Weiter" tippt. Ohne ihn passiert dort nichts, und
 *  wer nichts sieht, hält die Seite für kaputt (Usability-Review, 08.10.2026). */
export const HINWEIS_ZAHL_FEHLT = "Bitte wähle noch, wie viele Personen oder Tiere zu sehen sind.";
